<template>
  <div class="-my-2 sm:-mx-6 lg:-mx-8">
    <div class="py-2 align-middle inline-block min-w-full sm:px-2 lg:px-8">
      <slot name="filters"></slot>
      <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50" v-if="count > 0">
            <tr>
              <th
                v-for="(col, index) in columns"
                :key="col + index"
                class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap text-uppercase"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="count < 1">
              <td
                :colspan="columns.length"
                class="bg-white px-2 py-4 whitespace-nowrap text-sm text-center font-medium text-gray-500"
              >
                <i class="fas fa-folder-open mr-1"></i>
                Aucune tache de production trouver
              </td>
            </tr>
            <tr
              v-for="(item, index) in modelItems"
              :key="`${item.id}-${index}`"
              class="bg-white"
              :class="{ 'bg-gray-50': index % 2 }"
            >
              <td class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                <input
                  v-if="!item.ending_datetime"
                  type="checkbox"
                  class="text-xl rounded text-indigo-600 mr-1 cursor-pointer"
                  v-model="selected[item.production_id]"
                />
                # {{ item.production_id }}
              </td>
              <td v-if="editDescription === item?.production_id" colspan="5" class="p-2">
                <textarea
                  rows="6"
                  v-model="item.description"
                  class="block p-1 w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                ></textarea>
                <button
                  @click="setDescription(item)"
                  class="mt-2 inline-flex justify-center py-1 px-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Sauvegarder
                </button>
              </td>
              <td
                v-show="editDescription !== item?.production_id"
                class="px-2 py-4 whitespace-nowrap text-sm text-gray-500"
              >
                <span v-if="!item.production_doc_numbers">{{ prefixes[item.type] }}-{{ item.doc_number }}</span>
                <span v-else v-for="n in item.production_doc_numbers || []" :key="n" class="block pb-1">{{ n }}</span>
              </td>
              <td
                v-show="editDescription !== item?.production_id"
                class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                <router-link :to="`/clients/${item.client_id}`" class="text-gray-600 hover:text-indigo-400">
                  <p class="block font-bold">{{ item.name }}</p>
                  <p class="block">{{ item.vat }}</p>
                </router-link>
              </td>
              <td
                v-show="editDescription !== item?.production_id"
                class="px-2 py-4 whitespace-nowrap text-sm font-base text-gray-500"
              >
                <p class="block">&euro;&nbsp;{{ item.price_excl }} HTVA</p>
              </td>
              <td
                v-show="editDescription !== item?.production_id"
                class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                <ProductionStatus :status="item.status" @change="setStatus($event, item)" />
                <div class="pt-1">
                  <span class="block" v-if="item.creation_datetime"
                    >Creer le: {{ formatDateTime(item.creation_datetime) }}</span
                  >
                  <span class="block" v-if="item.waiting_datetime"
                    >En attente: {{ formatDateTime(item.waiting_datetime) }}</span
                  >
                  <span class="block" v-else-if="item.ending_datetime"
                    >Fini le: {{ formatDateTime(item.ending_datetime) }}</span
                  >
                  <span class="block" v-else-if="item.updated_datetime !== item.creation_datetime">
                    Dernier update le: {{ formatDateTime(item.updated_datetime) }}
                  </span>
                </div>
              </td>
              <td>
                <UIAtomSelectStage v-model="modelItems[index]"></UIAtomSelectStage>
              </td>
              <td v-show="editDescription !== item?.production_id" class="text-right pr-8">
                <router-link
                  v-if="item?.production_id"
                  :to="`/production/${item.production_id}`"
                  target="_blank"
                  class="text-indigo-600 hover:text-indigo-900 block"
                >
                  Voir
                </router-link>
                <button
                  v-if="item?.production_id"
                  @click="editDescription = item.production_id"
                  class="text-indigo-600 hover:text-indigo-900 whitespace-nowrap"
                >
                  <i class="fas fa-edit"></i> Description
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <router-link
        v-if="selectedIds.length"
        class="mt-4 ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        :to="`/production/${selectedIds.join(',')}`"
        target="_blank"
      >
        Ouvrir les selectioner (&euro;&nbsp;{{ selectedPrice }} HTVA)
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, defineProps, defineEmits } from 'vue'
import { useVModel } from '@vueuse/core'
import ProductionStatus from '@/components/UI/Atom/ProductionStatus'
import UIAtomSelectStage from '@/components/UI/Atom/SelectStage.vue'
import useApi from '@/hooks/useApi'
import useFunctions from '@/hooks/useFunctions'

const props = defineProps({
  count: {
    type: Number,
    default: 1,
  },
  items: {
    type: Array,
    default() {
      return []
    },
  },
})

const emit = defineEmits(['update:items'])
const modelItems = useVModel(props, 'items', emit, { passive: true })

const functions = useFunctions()
const formatDateTime = functions.formatDateTime

const selected = ref({})
const editDescription = ref(0)
const prefixes = {
  INVOICE: 'FA',
  QUOTE: 'DV',
  CREDIT_NOTE: 'NC',
}
const columns = ['Numéro', 'Créer depuis', 'Client', 'Prix', 'Status', 'Etape', '']
const selectedIds = computed(() => Object.keys(selected.value || {}).filter(k => !!selected.value[k]))
const selectedPrice = computed(() => {
  const number = selectedIds.value
    .map(id => (modelItems.value || []).find(item => String(item.production_id) === id))
    .reduce((prev, next) => {
      if (next && next.price_excl) {
        return prev + Number(next.price_excl)
      }
      return prev
    }, 0)

  return number.toFixed(2)
})

onMounted(() => {
  selected.value = {}
})

const api = useApi()
const setStatus = (status, item) => {
  api.production.setStatus({ id: item.production_id || item.id, status }).then(() => {
    item.status = status
    const now = new Date().toISOString()
    item.waiting_datetime = status === 'WAITING_RESPONSE' ? now : null
    item.ending_datetime = status === 'COMPLETED' || status === 'CANCELED' ? now : null
    item.updated_datetime = now
  })
}

const setDescription = item => {
  api.production
    .setDescription({
      id: item.production_id || item.id,
      description: item.description,
    })
    .then(() => {
      const now = new Date().toISOString()
      item.updated_datetime = now
      editDescription.value = 0
    })
}
</script>

<style></style>
